// ============================================================================
// PsyManualResultDialog — ручной протокол внешней (авторской) методики.
// Движок опросов ядра: SubmitPollResult (балл и/или заключение, дата). Для
// методик с онлайн-вопросами доступен как вспомогательный путь (бумажный бланк).
// ============================================================================

import { useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToastStore } from '@/store/toastStore';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import type { PsyMethod } from '@/lib/psyWorkspace';

export function PsyManualResultDialog({ patientId, method, onClose }: {
  patientId: string;
  method: PsyMethod;
  onClose: () => void;
}) {
  const showError = useToastStore((s) => s.showError);
  const [score, setScore] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const trimmedScore = score.trim();
    if (trimmedScore === '' && conclusion.trim() === '') {
      setError('Укажите балл или заключение');
      return;
    }
    if (trimmedScore !== '' && Number.isNaN(Number(trimmedScore))) {
      setError('Балл должен быть числом');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await getSDK().poll.submitPollResult({
        pollId: method.templateId,
        subjectId: patientId,
        score: trimmedScore === '' ? undefined : Number(trimmedScore),
        conclusion: conclusion.trim(),
        date,
      });
      onClose();
    } catch (e) {
      logErrorToJournal(e, `Психолог: ручной результат ${method.code}`);
      setError(friendlyError(e));
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[12000] bg-black/50 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-[min(520px,96vw)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Результат: {method.synonym}</span>
        </div>
        <div className="p-4 space-y-3">
          {error && <div className="text-xs text-destructive border border-destructive/40 rounded px-2 py-1.5">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px]">Балл (необязательно)</Label>
              <Input value={score} onChange={(e) => setScore(e.target.value)} placeholder="например, 14" className="h-8" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Дата</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Заключение / интерпретация</Label>
            <textarea value={conclusion} onChange={(e) => setConclusion(e.target.value)} rows={5}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm resize-y"
              placeholder="Интерпретация специалиста по лицензированному руководству" />
          </div>
        </div>
        <div className="px-4 py-2.5 border-t flex justify-end gap-2">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button size="sm" className="h-7 text-[11px]" onClick={() => void submit()} disabled={busy}>
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Сохранить результат
          </Button>
        </div>
      </div>
    </div>
  );
}
